// src/sim/world.ts — P1 expands to accept an optional spawn position
// (from MapData.playerSpawn). Single entity 'e0' remains (P1 scope;
// multi-entity lands in P3+).
//
// P2 adds torchOn (D-22, starts false), lightsState (D-16, empty {}), and
// playerFov (D-16, null) to the initial world state.
//
// P3 D-01..D-06/D-31/D-32: WorldState gains round: RoundState; Entity gains
// characterId/role/status/stamina/escort/hide/foxguard/peekSuppressedUntilRelease/
// teamSwitchTarget fields. 'e0' stays the player id (D-02) so the golden-hash
// rebake remains single-cause. Plan 01 is the SOLE owner of edits to
// src/sim/world.ts — the full Entity default literal (including characterId,
// peekSuppressedUntilRelease, teamSwitchTarget) lives here at the end of Plan 01.
//
// Entity id note: the single P1 entity is named 'e0' (NOT 'player') to keep
// the golden-hash rebake in this plan a single-cause diff. A rename to
// 'player' was considered in 01-RESEARCH.md Open Question 5 and explicitly
// deferred to P3 when multi-entity lands naturally. Tests, GameScene,
// and Perception construction all hard-code 'e0' — do not change without
// co-updating all of them AND re-baking the golden hash again.

import type { WorldState, Entity, Vec2, EntityRole, RoundState } from '../shared/types/world';
import type { MapData } from '../shared/types/map';
import { createRng } from './rng';
import { initLighting } from './lighting';
import { initAiController } from './controllers/registry';
import { resetP4CachesForNewSim } from './ai/p4-caches';
import { P3_HEADSTART_DURATION_TICKS, P3_ROUND_DURATION_TICKS } from './round/constants';

/**
 * P3 Plan 01 Task 3 — role assignment for a single entity at round start.
 *
 * `id` is the stable entity id (e.g. 'e0', 'hunter-0', 'hider-0').
 * `characterId` is the stable character profile id (e.g. 'charlie', 'mary', 'benny')
 * which PERSISTS across team-switch (D-31 GAME-12) — only `role` flips at team-switch.
 */
export interface RoleSpec {
  id: string;
  role: EntityRole;
  characterId: string;
  spawn?: Vec2;
}

/**
 * P3 Plan 01 Task 3 — options bag for createInitialWorldState.
 *
 * `roles` must contain exactly one entity with id='e0' (D-02) — this keeps
 * the golden-hash rebake single-cause (no entity-id rename).
 *
 * `mapData` is optional; when provided, its `playerSpawn` is used as a fallback
 * spawn for entities that don't specify their own. `zones` is accepted as
 * `unknown` because the zone-center-based spawn resolution for hunters lands
 * in Plan 02; Plan 01 only wires the playerSpawn fallback.
 */
export interface WorldInitOptions {
  rootSeed: number;
  roles: RoleSpec[];
  mapData?: MapData | { playerSpawn: Vec2 | null; zones?: unknown } | null;
  /** Optional fallback spawn for 'e0' — preserves the P1/P2 createInitialWorldState(rootSeed, spawn) convenience shape. */
  playerSpawn?: Vec2;
}

// Round-duration constants sourced from src/sim/round/constants.ts (resolving
// the TODO(Plan 02) markers from the original world.ts stub).
const HEADSTART = P3_HEADSTART_DURATION_TICKS;
const ROUND = P3_ROUND_DURATION_TICKS;

/**
 * P3 Plan 01 Task 3 — build a multi-entity WorldState with a RoundState
 * initialised at phase='headstart'.
 *
 * Every entity carries the complete Phase 3 Entity shape (characterId,
 * peekSuppressedUntilRelease, teamSwitchTarget + all other P3 additions)
 * so downstream Wave-2 rule modules don't need secondary Entity edits.
 */
export function createInitialWorldState(options: WorldInitOptions): WorldState {
  const { rootSeed, roles, mapData } = options;

  // Reset all P4 module-level round caches so replay-equality holds even when
  // the same rngSeed is reused across test runs (determinism invariant, Plan 10).
  resetP4CachesForNewSim();

  // Build entity map. 'e0' MUST be present (D-02) — enforced by convention
  // in the caller; the sim doesn't hard-check here to allow Vitest fixtures
  // with other id sets.
  const entities: Record<string, Entity> = {};
  for (const spec of roles) {
    const spawn = spec.spawn ?? mapData?.playerSpawn ?? { x: 0, y: 0 };
    entities[spec.id] = {
      id: spec.id,
      characterId: spec.characterId,
      position: { ...spawn },
      facing: 0,
      torchOn: false,
      role: spec.role,
      status: 'free',
      escortingEntityId: null,
      escortedBy: null,
      hidingSpotId: null,
      floorSpot: null,
      peekEndTick: null,
      peekSuppressedUntilRelease: false,
      stamina: 1,
      recentDropOffTick: null,
      foxguardConditionsStreakStartTick: null,
      lastFoxguardShoutTick: null,
      freedImmunityUntilTick: null,
      inTeamswitchTransition: false,
      teamSwitchTarget: null,
      lastPosition: null,
      // P4 additions (D-17, D-05, D-34):
      fear: 0,
      nextDecideTick: 0,
      checkedHidingSpotIds: [],
    };
  }

  // BLOCKER-3: Spawn dog + adult sim entities from mapData (P4 Plan 10, NPC-01..NPC-07).
  // Dog/adult AIControllers are registered here so tick.ts Stage 16 can call getAiController(id).
  // NOTE: The caller (GameScene) should emit 'round-setup-propose' on renderBus after calling
  // createInitialWorldState so RoundSetup.tsx can seed its drag-drop roster (round-setup-propose).
  if (mapData && 'dogs' in mapData && Array.isArray(mapData.dogs)) {
    for (const dogAuth of mapData.dogs) {
      entities[dogAuth.id] = {
        id: dogAuth.id,
        characterId: `dog-${dogAuth.id}`,
        position: { ...dogAuth.position },
        facing: 0,
        torchOn: false,
        role: 'dog',
        status: 'free',
        escortingEntityId: null,
        escortedBy: null,
        hidingSpotId: null,
        floorSpot: null,
        peekEndTick: null,
        peekSuppressedUntilRelease: false,
        stamina: 1,
        recentDropOffTick: null,
        foxguardConditionsStreakStartTick: null,
        lastFoxguardShoutTick: null,
        freedImmunityUntilTick: null,
        inTeamswitchTransition: false,
        teamSwitchTarget: null,
        lastPosition: null,
        fear: 0,
        nextDecideTick: 0,
        checkedHidingSpotIds: [],
      };
      initAiController({ entityId: dogAuth.id, rngSeed: rootSeed, initialRole: 'dog' });
    }
  }

  if (mapData && 'adults' in mapData && Array.isArray(mapData.adults)) {
    for (const adultAuth of mapData.adults) {
      const facingRad = (adultAuth.facingDeg * Math.PI) / 180;
      entities[adultAuth.id] = {
        id: adultAuth.id,
        characterId: `adult-${adultAuth.id}`,
        position: { ...adultAuth.position },
        facing: facingRad,
        torchOn: false,
        role: 'adult',
        status: 'free',
        escortingEntityId: null,
        escortedBy: null,
        hidingSpotId: null,
        floorSpot: null,
        peekEndTick: null,
        peekSuppressedUntilRelease: false,
        stamina: 1,
        recentDropOffTick: null,
        foxguardConditionsStreakStartTick: null,
        lastFoxguardShoutTick: null,
        freedImmunityUntilTick: null,
        inTeamswitchTransition: false,
        teamSwitchTarget: null,
        lastPosition: null,
        fear: 0,
        nextDecideTick: 0,
        checkedHidingSpotIds: [],
      };
      initAiController({ entityId: adultAuth.id, rngSeed: rootSeed, initialRole: 'adult' });
    }
  }

  const totalHiders = roles.filter((r) => r.role === 'hider').length;

  const round: RoundState = {
    phase: 'headstart',
    startTick: 0,
    headstartEndTick: HEADSTART,
    endTick: HEADSTART + ROUND,
    endReason: null,
    roundNumber: 1,
    caughtCount: 0,
    totalHiders,
  };

  // P2 D-14 / Pitfall 2: ROT.RNG is a process-wide singleton. Seed it ONCE per
  // round from the SimRng.motionLights sub-seed before any computeTorchFov() call
  // so determinism is preserved across runs and across Vitest workers. Building
  // the SimRng tree here also proves the sub-seed branch is reachable from boot.
  const rng = createRng(rootSeed);
  initLighting(rng);

  return {
    tick: 0,
    entities,
    rngSeed: rootSeed,
    lightsState: {}, // P2 D-16 — no motion lights active at round start
    playerFov: null, // P2 D-22 — torch off means no FOV bitset
    round,
    // P3 D-37 / Pitfall 6: derived per-tick hide-candidate slot. tick() writes
    // this at stage 11 (see src/sim/tick.ts) reusing the single-compute e0 value
    // from stage 13a (WARNING-7 guarantee).
    derived: { hideCandidate: null },
    // P4 D-13: per-team blackboards — empty at round start; reset at every round-started.
    teamBlackboards: {
      hunter: { entries: [], nextId: 0 },
      hider: { entries: [], nextId: 0 },
    },
    // P4 D-28: no adult commentary yet this session.
    lastAdultCommentaryTick: null,
    // P4 D-39: no NPC benched yet (first round).
    lastBenchedNpcId: null,
  };
}

/**
 * P2 back-compat helper: single-entity world for legacy callers (P0/P1/P2 tests
 * + GameScene). Builds 'e0' as a HIDER with characterId='charlie', 0 total
 * hunters, roundNumber=1. Callers that need a multi-entity world use
 * createInitialWorldState(options) directly.
 */
export function createDefaultWorldState(rootSeed: number, spawn?: Vec2): WorldState {
  const spec: RoleSpec =
    spawn !== undefined
      ? { id: 'e0', role: 'hider', characterId: 'charlie', spawn }
      : { id: 'e0', role: 'hider', characterId: 'charlie' };
  const options: WorldInitOptions =
    spawn !== undefined
      ? { rootSeed, roles: [spec], playerSpawn: spawn }
      : { rootSeed, roles: [spec] };
  return createInitialWorldState(options);
}

export type { WorldState, Entity };
